/**
 * Spanish glosses for the lexicon's measured terms.
 *
 * The corpus tokens stay in English everywhere they are displayed — the
 * archives are English-language and every count belongs to the English
 * stem — but on the Spanish site each term carries a tooltip gloss so
 * readers are never left guessing. Terms whose Spanish form is identical
 * (video, festival, manitoba, …) are simply absent from the map.
 */

export const TERM_GLOSSES: Record<string, string> = {
  // ── Cultural practices ────────────────────────────────────────────
  media: "medios",
  testimony: "testimonio",
  practice: "práctica",
  ceremony: "ceremonia",
  story: "relato",
  writing: "escritura",
  image: "imagen",
  commemoration: "conmemoración",
  photograph: "fotografía",
  historical: "histórico",
  film: "película",
  picture: "imagen",
  art: "arte",
  workshop: "taller",
  moving: "en movimiento (imagen)",
  monument: "monumento",
  exhibition: "exposición",
  television: "televisión",
  exhibit: "exhibición",
  documentary: "documental",
  song: "canción",
  drawing: "dibujo",
  music: "música",
  archive: "archivo",
  museum: "museo",
  literature: "literatura",
  narrative: "narrativa",
  poem: "poema",
  dance: "danza",
  theatre: "teatro",
  theater: "teatro",
  installation: "instalación",
  singing: "canto",
  gallery: "galería",
  poetry: "poesía",
  painting: "pintura",
  craft: "artesanía",
  novel: "novela",
  drum: "tambor",
  graphic: "gráfico",
  photography: "fotografía",
  memoir: "memorias",
  curator: "curaduría",
  sculpture: "escultura",
  carving: "talla",
  storytelling: "narración oral",
  quilt: "colcha",
  regalia: "atuendo ceremonial",
  comic: "cómic",
  pottery: "cerámica",
  beadwork: "trabajo de abalorios",

  // ── Human rights activism ─────────────────────────────────────────
  protest: "protesta",
  rights: "derechos",
  public: "público",
  petition: "petición",
  movement: "movimiento",
  campaign: "campaña",
  march: "marcha",
  demonstration: "manifestación",
  activism: "activismo",
  solidarity: "solidaridad",
  defense: "defensa",
  "social movement": "movimiento social",
  rally: "concentración",
  "human rights": "derechos humanos",
  resistance: "resistencia",
  advocate: "defensor",
  witness: "testigo",
  coalition: "coalición",
  advocacy: "incidencia",
  awareness: "concienciación",
  confront: "confrontar",
  accountability: "rendición de cuentas",
  promotion: "promoción",
  boycott: "boicot",
  mobilization: "movilización",
  empowerment: "empoderamiento",
  "direct action": "acción directa",
  denunciation: "denuncia",
  reckoning: "ajuste de cuentas",

  // ── Transitional justice ──────────────────────────────────────────
  report: "informe",
  justice: "justicia",
  peace: "paz",
  conflict: "conflicto",
  treaty: "tratado",
  prosecution: "enjuiciamiento",
  compensation: "indemnización",
  truth: "verdad",
  impunity: "impunidad",
  reconciliation: "reconciliación",
  disappearance: "desaparición",
  amnesty: "amnistía",
  memory: "memoria",
  genocide: "genocidio",
  settlement: "acuerdo",
  redress: "resarcimiento",
  reparation: "reparación",
  apology: "disculpa",
  healing: "sanación",
  prevention: "prevención",
  atrocity: "atrocidad",
  sovereignty: "soberanía",
  symbolic: "simbólico",
  survival: "supervivencia",
  transitional: "transicional",
  repair: "reparar",
  "truth commission": "comisión de la verdad",
  forgiveness: "perdón",
  restitution: "restitución",
  decolonization: "descolonización",
  intergenerational: "intergeneracional",
  recurrence: "repetición",
  restorative: "restaurativo",
  "residential school": "internado indígena",
  wellness: "bienestar",

  // ── Geography (only where the Spanish name differs) ───────────────
  mexico: "México",
  canada: "Canadá",
  brazil: "Brasil",
  "south africa": "Sudáfrica",
  philippines: "Filipinas",
  germany: "Alemania",
  cambodia: "Camboya",
  peru: "Perú",
  korea: "Corea",
  kenya: "Kenia",
  spain: "España",
  haiti: "Haití",
  norway: "Noruega",
  tunisia: "Túnez",
  lebanon: "Líbano",
  rwanda: "Ruanda",
  morocco: "Marruecos",
  ireland: "Irlanda",
  bogota: "Bogotá",
  cyprus: "Chipre",
  "timor leste": "Timor Oriental",
  "british columbia": "Columbia Británica",
  "sierra leone": "Sierra Leona",
  yukon: "Yukón",

  // ── Social actors ─────────────────────────────────────────────────
  activist: "activista",
  family: "familia",
  community: "comunidad",
  women: "mujeres",
  victim: "víctima",
  children: "niñez",
  prisoner: "prisionero",
  youth: "juventud",
  mother: "madre",
  survivor: "sobreviviente",
  woman: "mujer",
  population: "población",
  indigenous: "indígena",
  detainee: "detenido",
  father: "padre",
  soldier: "soldado",
  perpetrator: "perpetrador",
  church: "iglesia",
  refugee: "refugiado",
  commissioner: "comisionado",
  elder: "anciano",
  artist: "artista",
  veteran: "veterano",
  displaced: "desplazado",
  race: "raza",
  native: "nativo",
  widow: "viuda",
  settler: "colono",
  "internally displaced": "desplazado interno",
  "first nations": "primeras naciones",
  aboriginal: "aborigen",
  missionary: "misionero",
  orphan: "huérfano",
  healer: "sanador",

  // ── Institutions & services ───────────────────────────────────────
  government: "gobierno",
  court: "tribunal",
  center: "centro",
  commission: "comisión",
  education: "educación",
  health: "salud",
  university: "universidad",
  school: "escuela",
  agency: "agencia",
  business: "empresa",
  centre: "centro",
  training: "capacitación",
  ministry: "ministerio",
  foundation: "fundación",
  institute: "instituto",
  funding: "financiamiento",
  employment: "empleo",
  welfare: "bienestar social",
  college: "instituto superior",
  clinic: "clínica",
  "mental health": "salud mental",
  library: "biblioteca",
  charity: "organización benéfica",
  nonprofit: "sin fines de lucro",
  "child welfare": "bienestar infantil",
};

/**
 * Tooltip text for a term: "term — gloss" on the Spanish site, nothing
 * on other locales or for terms whose Spanish form is identical.
 */
export function glossTitle(
  term: string,
  language: string
): string | undefined {
  if (!language?.startsWith("es")) return undefined;
  const gloss = TERM_GLOSSES[term.toLowerCase()];
  return gloss ? `${term} — ${gloss}` : undefined;
}
